/**
 * The editor action behind "Save as asset" (CONTENT_MODEL_SPEC §3.5).
 *
 * Kept apart from `save-as-asset.ts` so that module stays pure (the multipart
 * over POJOs, unit-testable with a stub client) while the scene surgery —
 * which needs a live viewer and the undo stack — lives here.
 *
 * The gesture is: promote the selection into an asset folder, create the
 * asset, and then, by default, **replace what was promoted with a reference
 * to it** — one node whose `src` is `{apiBase}/assets/{id}/file` (§3.4). That
 * replacement is the point: after it, the scene names the component instead of
 * carrying a copy of it, and editing the asset reaches every scene that names
 * it. `replaceWithReference: false` leaves the originals inline for the author
 * who wanted a snapshot in the library and their own objects untouched.
 *
 * Promotion itself never restructures the live scene (spec §3.5, and #811's
 * "grouping is document-only"): a multi-selection is grouped under a
 * synthesized root **in the exported document**, and the editor keeps its own
 * undo story. So the whole visible change is this module's — one undo entry
 * covering the deletes and the reference that took their place.
 */

import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { TraceViewerElement } from 'trace-viewer';
import type { SceneObject } from 'trace-viewer/scene';
import { assetFileUrl } from 'trace-viewer/content';
import { CompositeCommand, CreateObjectCommand, getUndoManager, type UndoCommand, isStructuralRoot, topLevelParent } from 'trace-viewer/editor';
import { Assets } from 'trace-viewer/cms';
import { apiConfig, API_BASE_URL, BLOB_BASE_URL, getCatalogToken, getCatalogSelectedFolderId, getCatalogSelectedTeamId, LS_TEAM_ID_KEY } from '../catalog-shared';
import { topMostSelected } from '../selection-utils';
import { buildDeleteCommand } from '../undo-capture';
import { promotionManifestOptions } from './scene-folder';
import { uploadPromotedAsset, type AssetCreateClient, type AssetFolderTarget } from './save-as-asset';

export interface SaveAsAssetOptions {
    /** Asset title, and the name of the synthesized root when several objects are promoted. */
    name?: string;
    /**
     * Replace the promoted objects with a single reference node (default).
     * False keeps them inline: the asset is created, the scene is untouched.
     */
    replaceWithReference?: boolean;
    /** Folder + team to create the asset in. Defaults to the open catalog folder. */
    target?: AssetFolderTarget;
    /** Injected for tests; the generated `Assets` client otherwise. */
    client?: AssetCreateClient;
}

export interface SaveAsAssetResult {
    assetId: string;
    /** `{apiBase}/assets/{id}/file` — what a reference node carries (§3.4). */
    referenceUrl: string;
    /** The reference that replaced the promotion, or null when the originals were kept. */
    reference: SceneObject | null;
    /**
     * The asset's title.
     *
     * Not necessarily the reference node's name: a placed reference takes its
     * name from the referenced document's own root node, which for a
     * single-object promotion is that object's name rather than the title the
     * author typed.
     */
    name: string;
    /** How many files travelled into the new asset folder. */
    fileCount: number;
}

/**
 * The folder a promotion lands in: the one open in the Studio panel.
 *
 * Read at the call, not stored — the same rule "Add to catalog" already
 * follows, so a promotion goes where the author is looking.
 */
export function currentAssetFolderTarget(): AssetFolderTarget | null {
    const folderId = getCatalogSelectedFolderId();
    if (!folderId) return null;
    const teamId = getCatalogSelectedTeamId() ?? localStorage.getItem(LS_TEAM_ID_KEY);
    if (!teamId) return null;
    return { folderId, teamId };
}

/** Whether the editor is in a state where a promotion could succeed at all. */
export function canSaveAsAsset(): boolean {
    return Boolean(getCatalogToken()) && currentAssetFolderTarget() !== null;
}

/**
 * Promote `roots` into a new library asset.
 *
 * Throws — with the offending `files[]` entries in the message
 * ({@link PromotionRefusedError}) when the closure rule refuses the selection,
 * and plainly otherwise. Nothing is uploaded and the scene is untouched in
 * either case; the caller is what turns the throw into a toast.
 */
export async function saveSelectionAsAsset(
    viewer: TraceViewerElement,
    roots: readonly SceneObject[],
    options: SaveAsAssetOptions = {},
): Promise<SaveAsAssetResult> {
    const traceScene = viewer.traceScene;
    if (!traceScene) throw new Error('No scene is open.');

    // An ancestor already carries its selected descendants, and neither root
    // is content — promoting one would be "save the scene", a different
    // gesture with a different destination (D8).
    // De-duplicated first: the same object can reach here twice (an agent
    // naming it twice), and `topMostSelected` prunes ancestors, not repeats —
    // a repeat would be captured and deleted twice.
    const targets = topMostSelected([...new Set(roots)].filter(object => !isStructuralRoot(object)));
    if (targets.length === 0) {
        throw new Error('Select the object(s) to save as an asset first.');
    }

    const target = options.target ?? currentAssetFolderTarget();
    if (!target) {
        throw new Error('Open a folder in the Studio panel first — that is the folder the asset is created in.');
    }

    const name = options.name?.trim() || targets[0].name;

    // The closure rule is not passed in: `promoteToAsset` forces it (§3.2).
    // What is passed is the two bases that make the *other* classes decidable
    // — without `apiBaseUrl` there is no library-reference class at all and
    // every live reference in the selection comes back an offender.
    const promoted = await viewer.promoteSubtreeToAsset(targets, {
        name,
        manifest: promotionManifestOptions(API_BASE_URL, BLOB_BASE_URL),
    });

    const client = options.client ?? assetsClient();
    // Throws `PromotionRefusedError` when the closure rule refused anything —
    // before a byte is uploaded.
    const assetId = await uploadPromotedAsset(client, promoted, target, name);
    const referenceUrl = assetFileUrl(API_BASE_URL, assetId);

    const reference = options.replaceWithReference === false
        ? null
        : await replaceWithReference(viewer, targets, referenceUrl, name);

    // The Studio panel and the AI catalog index both listen for this; the new
    // asset is invisible until they re-read the folder.
    window.dispatchEvent(new CustomEvent('trace-catalog:folder-refresh'));

    console.log(
        `[SaveAsAsset] Created asset ${assetId} "${name}" from ${targets.length} object(s): `
        + `document (${promoted.document.size} bytes) + ${promoted.files.size} file(s)`
        + `${reference ? ', subtree replaced by a reference' : ', originals kept inline'}`,
    );

    return { assetId, referenceUrl, reference, name, fileCount: promoted.files.size };
}

/** The generated client, authenticated the way every other catalog call is. */
function assetsClient(): AssetCreateClient {
    const token = getCatalogToken();
    if (!token) throw new Error('Sign in to the Studio panel first.');
    return new Assets(apiConfig(token));
}

/**
 * Swap the promoted objects for one reference node, as a single undo entry.
 *
 * The reference stands exactly where the promotion did:
 *
 * - **One object promoted** — same parent, same local transform. The asset's
 *   root *is* that object (no synthesized group, spec §3.5), so the reference
 *   renders in its place with nothing moved.
 * - **Several** — the document's root is a synthesized group whose children
 *   were re-expressed in the scene root's frame, so the reference belongs at
 *   the scene root with an identity transform. Anything else would apply one
 *   object's placement twice.
 *
 * Undo order matters and is why this is one `CompositeCommand`: undo runs its
 * children in reverse, so the reference is removed first and the originals
 * come back afterwards, into the parents they were captured under.
 */
async function replaceWithReference(
    viewer: TraceViewerElement,
    targets: readonly SceneObject[],
    referenceUrl: string,
    name: string,
): Promise<SceneObject> {
    const traceScene = viewer.traceScene!;
    const single = targets.length === 1 ? targets[0] : null;
    const parent = single ? single.getParent() ?? null : null;
    const placement = single ? capturePlacement(single) : null;

    // Capture BEFORE deleting: a delete command restores from data read while
    // the objects are still in the scene.
    const captured = targets.map(target => ({ target, command: buildDeleteCommand(target) }));

    // Placed as a reference, exactly like a catalog drag (§3.8: import
    // defaults to reference, unpack is explicit). No `trace-catalog:import`
    // event — that would hand the node to the editor's adoption path and push
    // a second undo entry.
    const { sceneObject: reference } = await viewer.import(referenceUrl, name);

    // Only a delete that actually happened belongs in the undo entry. Undoing
    // a delete RESTORES its subtree, so carrying a refused one would put a
    // second copy of an object that never left into the scene.
    const deleteCommands: UndoCommand[] = [];
    for (const { target, command } of captured) {
        if (traceScene.deleteSceneObject(target)) deleteCommands.push(command);
        else console.error(`[SaveAsAsset] Could not remove "${target.name}" after promotion`);
    }

    if (parent) traceScene.reparentSceneObject(reference, parent);
    applyPlacement(reference, placement);

    if (!getUndoManager().isUndoRedoing) {
        reference.captureTransform();
        reference.refreshSceneData();
        const createCmd = new CreateObjectCommand(
            reference,
            parent ?? topLevelParent(traceScene),
            reference.getSceneObjectData(),
            [],
        );
        const commands: UndoCommand[] = [...deleteCommands, createCmd];
        getUndoManager().push(commands.length === 1
            ? createCmd
            : new CompositeCommand(`Save "${name}" as asset`, commands));
    }

    traceScene.select(reference);
    return reference;
}

/** A local transform, read off an object that is about to be deleted. */
interface Placement {
    position: [number, number, number];
    rotation: [number, number, number];
    quaternion: [number, number, number, number] | null;
    scaling: [number, number, number];
}

function capturePlacement(object: SceneObject): Placement {
    const t = object.transform;
    return {
        position: [t.position.x, t.position.y, t.position.z],
        rotation: [t.rotation.x, t.rotation.y, t.rotation.z],
        quaternion: t.rotationQuaternion
            ? [t.rotationQuaternion.x, t.rotationQuaternion.y, t.rotationQuaternion.z, t.rotationQuaternion.w]
            : null,
        scaling: [t.scaling.x, t.scaling.y, t.scaling.z],
    };
}

/**
 * Put the reference where the promotion stood.
 *
 * With no placement (a multi-object promotion) the reference is reset to
 * identity: the document's group root is the scene-root frame itself, so the
 * arrangement inside the asset already carries every offset the author made.
 */
function applyPlacement(reference: SceneObject, placement: Placement | null): void {
    const t = reference.transform;
    if (!placement) {
        t.position.set(0, 0, 0);
        t.rotationQuaternion = null;
        t.rotation.set(0, 0, 0);
        t.scaling.set(1, 1, 1);
        return;
    }
    t.position.set(...placement.position);
    if (placement.quaternion) {
        const [x, y, z, w] = placement.quaternion;
        if (t.rotationQuaternion) t.rotationQuaternion.set(x, y, z, w);
        else t.rotationQuaternion = new Quaternion(x, y, z, w);
    } else {
        t.rotationQuaternion = null;
        t.rotation.set(...placement.rotation);
    }
    t.scaling.set(...placement.scaling);
}
